/**
 * PeopleCard - Card "Pessoas" da sidebar T1 (Responsável, Equipe, Seguidores).
 *
 * Responsável/Equipe: popovers portados 1:1 do `TicketDetailSidebar` legado.
 * Seguidores: NOVO nesta task — usa os hooks/`FollowerSelector` já existentes
 * (`useTicketFollowers`/`useAddTicketFollower`/`useRemoveTicketFollower`,
 * criados numa task anterior mas nunca conectados na página do ticket; só
 * consumidos hoje pelo `TicketFormDrawer` na criação). O comentário desse
 * drawer já apontava "Seguidores são gerenciados na página do ticket" — esta
 * é essa conexão.
 */
import React, { useMemo, useState } from 'react';
import { Input } from '@/shared/components/ui';
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/components/ui/avatar';
import { SidebarCard } from './SidebarCard';
import { SidebarField } from '../SidebarField';
import { TicketFieldPopover } from '../TicketFieldPopover';
import { FollowerSelector } from '../FollowerSelector';
import type { FollowerItem } from '../FollowerSelector';
import { useTicketFollowers, useAddTicketFollower, useRemoveTicketFollower } from '../../hooks/useTicketFollowers';
import type { PopulatedTicket } from '../../types';
import { Icon } from '@/shared/icons';

export interface SidebarUser {
  id: string;
  name: string;
  picture?: string;
}

export interface SidebarTeam {
  id: string;
  name: string;
}

export interface PeopleCardProps {
  ticket: PopulatedTicket;
  users: SidebarUser[];
  teams: SidebarTeam[];
  onAssigneeChange: (assigneeId: string | null) => void;
  onTeamChange: (teamId: string | null) => void;
  loading?: boolean;
}

const MAX_FOLLOWER_AVATARS = 3;

export const PeopleCard: React.FC<PeopleCardProps> = ({
  ticket,
  users,
  teams,
  onAssigneeChange,
  onTeamChange,
  loading = false,
}) => {
  const [activePopover, setActivePopover] = useState<'assignee' | 'team' | 'followers' | null>(null);
  const [assigneeSearch, setAssigneeSearch] = useState('');
  const [teamSearch, setTeamSearch] = useState('');

  const resolvedAssignee = ticket.assignee || (ticket.assigneeId ? users.find((u) => u.id === ticket.assigneeId) : undefined) || null;
  const resolvedTeam = ticket.team || (ticket.teamId ? teams.find((t) => t.id === ticket.teamId) : undefined) || null;

  const togglePopover = (name: 'assignee' | 'team' | 'followers') => {
    setActivePopover((prev) => {
      if (prev === name) return null;
      if (name === 'assignee') setAssigneeSearch('');
      if (name === 'team') setTeamSearch('');
      return name;
    });
  };

  const filteredUsers = useMemo(() => {
    if (!assigneeSearch.trim()) return users;
    const q = assigneeSearch.toLowerCase();
    return users.filter((u) => u.name.toLowerCase().includes(q));
  }, [users, assigneeSearch]);

  const filteredTeams = useMemo(() => {
    if (!teamSearch.trim()) return teams;
    const q = teamSearch.toLowerCase();
    return teams.filter((t) => t.name.toLowerCase().includes(q));
  }, [teams, teamSearch]);

  // Seguidores — collection separada (`ticket-followers`), não campo do ticket.
  const { data: followersData = [] } = useTicketFollowers(ticket.id);
  const addFollowerMutation = useAddTicketFollower();
  const removeFollowerMutation = useRemoveTicketFollower();

  const followerItems: FollowerItem[] = useMemo(
    () =>
      followersData.map((f) => ({
        id: f.followerId,
        type: f.followerType,
        name: f.followerName || (f.followerType === 'user' ? 'Usuário' : 'Contato'),
        email: f.followerEmail,
        picture: f.followerPicture,
      })),
    [followersData]
  );

  // `FollowerSelector` devolve o array completo (mesmo shape usado na criação
  // do ticket) — aqui traduzimos pra add/remove individual contra a API
  // dedicada de `ticket-followers` (diff entre estado anterior e novo).
  const handleFollowersChange = (next: FollowerItem[]) => {
    const prevKeys = new Set(followerItems.map((f) => `${f.type}:${f.id}`));
    const nextKeys = new Set(next.map((f) => `${f.type}:${f.id}`));

    for (const follower of next) {
      const key = `${follower.type}:${follower.id}`;
      if (!prevKeys.has(key)) {
        addFollowerMutation.mutate({
          ticketId: ticket.id,
          followerType: follower.type,
          followerId: follower.id,
          source: 'manual',
        });
      }
    }

    for (const follower of followerItems) {
      const key = `${follower.type}:${follower.id}`;
      if (!nextKeys.has(key)) {
        removeFollowerMutation.mutate({
          ticketId: ticket.id,
          followerType: follower.type,
          followerId: follower.id,
        });
      }
    }
  };

  const visibleFollowers = followerItems.slice(0, MAX_FOLLOWER_AVATARS);
  const extraFollowersCount = followerItems.length - visibleFollowers.length;

  return (
    <SidebarCard icon="users" title="Pessoas" collapsible data-testid="ticket-detail-people-card">
      {/* Assignee */}
      <div className="relative">
        <SidebarField
          label="Responsável"
          clickable
          onClick={() => togglePopover('assignee')}
          ariaLabel={`Responsável: ${resolvedAssignee?.name || 'Não atribuído'}`}
          data-testid="ticket-transfer-button"
        >
          {resolvedAssignee ? (
            <>
              <Avatar className="w-[18px] h-[18px]">
                {resolvedAssignee.picture && <AvatarImage src={resolvedAssignee.picture} />}
                <AvatarFallback className="text-[8px] font-bold">
                  {resolvedAssignee.name?.charAt(0)?.toUpperCase() || '?'}
                </AvatarFallback>
              </Avatar>
              <span className="text-xs font-medium" style={{ color: 'var(--theme-card-text)' }}>
                {resolvedAssignee.name}
              </span>
            </>
          ) : (
            <>
              <Icon name="user" className="w-3.5 h-3.5" style={{ color: 'var(--theme-muted-text)' }} />
              <span className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
                Não atribuído
              </span>
            </>
          )}
        </SidebarField>

        <TicketFieldPopover isOpen={activePopover === 'assignee'} onClose={() => setActivePopover(null)} width={260}>
          <div className="px-2 py-1.5" style={{ borderBottom: '1px solid #e5e7eb' }}>
            {/* Input shared (NUNCA nativo em dropdown) — tema claro garantido
             * pelo LIGHT_THEME_OVERRIDE do TicketFieldPopover (CSS vars). */}
            <Input
              type="text"
              value={assigneeSearch}
              onChange={(e) => setAssigneeSearch(e.target.value)}
              placeholder="Buscar usuário..."
              icon="search"
              iconPosition="left"
              autoFocus
              className="h-7 text-xs"
              data-testid="ticket-detail-assignee-search"
            />
          </div>
          <div className="py-1 max-h-48 overflow-y-auto">
            {ticket.assigneeId && (
              <button
                className="w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 transition-colors"
                style={{ color: '#ef4444' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#fef2f2'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                onClick={() => { onAssigneeChange(null); setActivePopover(null); }}
                disabled={loading}
              >
                <Icon name="x" className="w-3 h-3" />
                <span>Remover responsável</span>
              </button>
            )}
            {filteredUsers.map((user) => (
              <button
                key={user.id}
                data-testid={`ticket-transfer-user-option-${user.id}`}
                className="w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 transition-colors"
                style={{ color: '#1e293b' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                onClick={() => { onAssigneeChange(user.id); setActivePopover(null); }}
                disabled={loading}
              >
                <Avatar className="w-5 h-5">
                  {user.picture && <AvatarImage src={user.picture} />}
                  <AvatarFallback className="text-[8px] font-bold">
                    {user.name?.charAt(0)?.toUpperCase() || '?'}
                  </AvatarFallback>
                </Avatar>
                <span className="flex-1 truncate">{user.name}</span>
                {ticket.assigneeId === user.id && (
                  <Icon name="check" className="w-3 h-3 flex-shrink-0" style={{ color: '#8b5cf6' }} />
                )}
              </button>
            ))}
            {filteredUsers.length === 0 && (
              <div className="px-3 py-2 text-xs" style={{ color: '#94a3b8' }}>
                Nenhum usuário encontrado
              </div>
            )}
          </div>
        </TicketFieldPopover>
      </div>

      {/* Team */}
      <div className="relative">
        <SidebarField
          label="Equipe"
          clickable
          onClick={() => togglePopover('team')}
          ariaLabel={`Equipe: ${resolvedTeam?.name || 'Nenhuma equipe'}`}
          data-testid="ticket-transfer-team-button"
        >
          <Icon
            name="users"
            className="w-3.5 h-3.5"
            style={{ color: resolvedTeam ? 'var(--theme-card-text)' : 'var(--theme-muted-text)' }}
          />
          <span
            className="text-xs font-medium"
            style={{ color: resolvedTeam ? 'var(--theme-card-text)' : 'var(--theme-muted-text)' }}
          >
            {resolvedTeam?.name || 'Nenhuma equipe'}
          </span>
        </SidebarField>

        <TicketFieldPopover isOpen={activePopover === 'team'} onClose={() => setActivePopover(null)} width={240}>
          <div className="px-2 py-1.5" style={{ borderBottom: '1px solid #e5e7eb' }}>
            {/* Input shared (NUNCA nativo em dropdown) — tema claro garantido
             * pelo LIGHT_THEME_OVERRIDE do TicketFieldPopover (CSS vars). */}
            <Input
              type="text"
              value={teamSearch}
              onChange={(e) => setTeamSearch(e.target.value)}
              placeholder="Buscar equipe..."
              icon="search"
              iconPosition="left"
              autoFocus
              className="h-7 text-xs"
              data-testid="ticket-detail-team-search"
            />
          </div>
          <div className="py-1 max-h-48 overflow-y-auto">
            {ticket.teamId && (
              <button
                className="w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 transition-colors"
                style={{ color: '#ef4444' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#fef2f2'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                onClick={() => { onTeamChange(null); setActivePopover(null); }}
                disabled={loading}
              >
                <Icon name="x" className="w-3 h-3" />
                <span>Remover equipe</span>
              </button>
            )}
            {filteredTeams.map((team) => (
              <button
                key={team.id}
                data-testid={`ticket-transfer-team-option-${team.id}`}
                className="w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 transition-colors"
                style={{ color: '#1e293b' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                onClick={() => { onTeamChange(team.id); setActivePopover(null); }}
                disabled={loading}
              >
                <Icon name="users" className="w-3.5 h-3.5" style={{ color: '#64748b' }} />
                <span className="flex-1 truncate">{team.name}</span>
                {ticket.teamId === team.id && (
                  <Icon name="check" className="w-3 h-3 flex-shrink-0" style={{ color: '#8b5cf6' }} />
                )}
              </button>
            ))}
            {filteredTeams.length === 0 && (
              <div className="px-3 py-2 text-xs" style={{ color: '#94a3b8' }}>
                Nenhuma equipe encontrada
              </div>
            )}
          </div>
        </TicketFieldPopover>
      </div>

      {/* Followers */}
      <div className="relative">
        <SidebarField
          label="Seguidores"
          clickable
          onClick={() => togglePopover('followers')}
          ariaLabel={`Seguidores: ${followerItems.length}`}
          data-testid="ticket-detail-followers-field"
        >
          {followerItems.length > 0 ? (
            <div className="flex items-center -space-x-1.5">
              {visibleFollowers.map((follower) => (
                <Avatar key={`${follower.type}:${follower.id}`} className="w-[18px] h-[18px] ring-2 ring-white">
                  {follower.picture && <AvatarImage src={follower.picture} />}
                  <AvatarFallback className="text-[8px] font-bold">
                    {follower.name?.charAt(0)?.toUpperCase() || '?'}
                  </AvatarFallback>
                </Avatar>
              ))}
              {extraFollowersCount > 0 && (
                <span
                  className="w-[18px] h-[18px] rounded-full ring-2 ring-white flex items-center justify-center text-[8px] font-bold"
                  style={{ backgroundColor: '#ede9fe', color: '#7c3aed' }}
                >
                  +{extraFollowersCount}
                </span>
              )}
            </div>
          ) : (
            <span className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>Nenhum</span>
          )}
          <span className="text-xs font-medium" style={{ color: '#8b5cf6' }}>+</span>
        </SidebarField>

        <TicketFieldPopover isOpen={activePopover === 'followers'} onClose={() => setActivePopover(null)} width={300}>
          <div className="p-2">
            <FollowerSelector value={followerItems} onChange={handleFollowersChange} disabled={loading} />
          </div>
        </TicketFieldPopover>
      </div>
    </SidebarCard>
  );
};

PeopleCard.displayName = 'PeopleCard';
